import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { LogIn, Users, Activity as ActivityIcon, UserCheck } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const MODULES = {
  students: "Élèves", evaluations: "Évaluations", grades: "Notes", absences: "Absences", "vie-scolaire": "Vie scolaire",
  finance: "Finance", payments: "Paiements", expenses: "Dépenses", espace: "Espace enseignants", dashboard: "Tableaux de bord",
  classes: "Classes", subjects: "Matières", users: "Utilisateurs", settings: "Paramètres", auth: "Connexion", assiduite: "Assiduité",
  schools: "Écoles", stats: "Statistiques", admin: "Administration", me: "Compte", registrations: "Inscriptions", public: "Public",
};
const ROLE = { admin: "Admin", directeur: "Directeur", maitre: "Enseignant" };

function Card({ icon: Icon, label, value, testid }) {
  return (
    <div className="ek-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-[#5C736A]"><Icon className="w-4 h-4 text-[#1F7A63]" />{label}</div>
      <div className="text-2xl sm:text-3xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>{value ?? "—"}</div>
    </div>
  );
}

export function useActivity(enabled = true) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { if (enabled) api.get("/admin/activity").then(({ data }) => setD(data)).catch((e) => setErr(formatError(e))); }, [enabled]);
  return { d, err };
}

export function ActivityCharts({ d, compact }) {
  const mods = d.modules.filter((m) => !["auth", "me", "notifications", "racine"].includes(m.module)).slice(0, 10).map((m) => ({ ...m, nom: MODULES[m.module] || m.module }));
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="ek-card p-4">
        <div className="font-semibold text-sm mb-3">Connexions par jour (30 j)</div>
        <div className="h-56"><ResponsiveContainer><BarChart data={d.connexions_par_jour}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="jour" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="connexions" fill="#1F7A63" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
      </div>
      <div className="ek-card p-4">
        <div className="font-semibold text-sm mb-3">Utilisation des modules</div>
        <div className="h-56">
          {mods.length ? (
            <ResponsiveContainer><BarChart data={mods} layout="vertical" margin={{ left: 10 }}><XAxis type="number" hide /><YAxis type="category" dataKey="nom" width={110} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="requetes" name="Actions" fill="#E6B800" radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer>
          ) : <div className="h-full grid place-items-center text-sm text-[#5C736A]">Les statistiques d&apos;usage s&apos;afficheront au fil de l&apos;utilisation.</div>}
        </div>
      </div>
      {!compact && null}
    </div>
  );
}

export default function Activity() {
  const { d, err } = useActivity();
  if (err) return <div className="ek-card p-4 text-red-600 text-sm">{err}</div>;
  if (!d) return <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="skel h-24" />)}</div>;
  return (
    <div className="space-y-5" data-testid="activity-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Suivi des activités</h1>
        <p className="text-sm text-[#5C736A]">Connexions, utilisation des modules et statistiques par école sur les 30 derniers jours.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card icon={LogIn} label="Connexions (30 j)" value={d.connexions_total} testid="act-connexions" />
        <Card icon={Users} label="Utilisateurs connectés" value={d.utilisateurs_connectes} testid="act-users" />
        <Card icon={UserCheck} label="Comptes actifs" value={d.comptes_actifs} testid="act-actifs" />
        <Card icon={ActivityIcon} label="Écoles actives" value={d.par_ecole.filter((s) => s.activite > 0).length} testid="act-ecoles" />
      </div>
      <ActivityCharts d={d} />
      <div className="ek-card p-0 ek-table-wrap" data-testid="act-schools">
        <div className="px-4 py-3 border-b border-[#E2E8F0] font-semibold text-sm">Statistiques par école</div>
        <Table>
          <TableHeader><TableRow><TableHead>École</TableHead><TableHead className="hidden md:table-cell">IEF</TableHead><TableHead>Élèves</TableHead><TableHead>Enseignants</TableHead><TableHead>Classes</TableHead><TableHead>Activité (30 j)</TableHead></TableRow></TableHeader>
          <TableBody>{d.par_ecole.map((s) => (
            <TableRow key={s.id}><TableCell className="font-semibold">{s.nom}</TableCell><TableCell className="hidden md:table-cell text-sm">{s.ief || "—"}</TableCell><TableCell>{s.eleves}</TableCell><TableCell>{s.enseignants}</TableCell><TableCell>{s.classes}</TableCell><TableCell>{s.activite}</TableCell></TableRow>
          ))}</TableBody>
        </Table>
      </div>
      <div className="ek-card p-0 ek-table-wrap">
        <div className="px-4 py-3 border-b border-[#E2E8F0] font-semibold text-sm">Dernières connexions</div>
        <Table>
          <TableHeader><TableRow><TableHead>Utilisateur</TableHead><TableHead>Rôle</TableHead><TableHead>Date</TableHead><TableHead className="hidden md:table-cell">Adresse IP</TableHead></TableRow></TableHeader>
          <TableBody>{d.dernieres_connexions.map((c) => (
            <TableRow key={c.id}><TableCell className="font-medium">{c.username}</TableCell><TableCell>{ROLE[c.role] || "—"}</TableCell><TableCell className="text-sm">{new Date(c.at).toLocaleString("fr-FR")}</TableCell><TableCell className="hidden md:table-cell text-xs">{c.ip}</TableCell></TableRow>
          ))}</TableBody>
        </Table>
      </div>
    </div>
  );
}
